import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Header from "./Components/Header";
import { ChevronLeft, ChevronRight, Calendar, Clock } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PageContainer from "./Components/PageContainer";
import MainContent from "./Components/MainContent";
import BookingSummary from "./Components/BookingSummary";
import PaymentModal from "./Components/PaymentModal";
import { normalizeAvailabilityCollection, teacherApi } from "../../features/teacher/teacherApi";
import { getCanonicalYearId } from "../../utils/basicPlanAccess";
import { bookingService } from "../../services/booking.service";
import {
  persistBookingSummary,
  readBookingFlowCache,
  readStoredBookingSummary,
} from "./bookingFlowStorage";
import {
  normalizeAudPriceForDisplay,
  normalizeAudPriceToMinorUnits,
} from "../../utils/audCurrency";

const AUSTRALIA_TIME_ZONE = "Australia/Sydney";
const PAYMENT_UI_STATES = {
  IDLE: "idle",
  CREATING_CHECKOUT: "creating_checkout",
  REDIRECTING: "redirecting",
  CONFIRMING_PAYMENT: "confirming_payment",
  PAYMENT_CONFIRMED: "payment_confirmed",
  PAYMENT_FAILED_OR_CANCELLED: "payment_failed_or_cancelled",
};

const getAustraliaDateParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const mappedParts = parts.reduce((result, part) => {
    if (part.type !== "literal") {
      result[part.type] = part.value;
    }
    return result;
  }, {});

  return {
    year: Number(mappedParts.year),
    month: Number(mappedParts.month),
    day: Number(mappedParts.day),
  };
};

const formatMonthLabel = (monthDate) =>
  monthDate.toLocaleDateString("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    month: "long",
    year: "numeric",
  });

const formatDateKey = (date) => {
  const { year, month, day } = getAustraliaDateParts(date);
  const normalizedMonth = `${month}`.padStart(2, "0");
  const normalizedDay = `${day}`.padStart(2, "0");

  return `${year}-${normalizedMonth}-${normalizedDay}`;
};

const formatAustralianTime = (date) =>
  date.toLocaleTimeString("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const formatAustralianDateLabel = (date) =>
  date.toLocaleDateString("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const parseSlotDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatSlotTimeRange = (slot) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  const endDate = parseSlotDate(slot?.end_time || slot?.endTime);

  if (!startDate || !endDate) return "Time unavailable";

  const startLabel = formatAustralianTime(startDate);
  const endLabel = formatAustralianTime(endDate);

  return `${startLabel} - ${endLabel}`;
};

const getSlotDurationLabel = (slot) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  const endDate = parseSlotDate(slot?.end_time || slot?.endTime);

  if (!startDate || !endDate) return "60 minutes";

  const durationInMinutes = Math.max(
    0,
    Math.round((endDate.getTime() - startDate.getTime()) / 60000)
  );

  return `${durationInMinutes || 60} minutes`;
};

const buildAvailabilityModel = (slots) => {
  const groupedSlots = new Map();

  slots.forEach((slot) => {
    const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
    if (!startDate) return;

    const dateKey = formatDateKey(startDate);
    const { year, month, day } = getAustraliaDateParts(startDate);
    const monthKey = `${year}-${month}`;
    const monthDate = new Date(year, month - 1, 1);
    const slotEntry = {
      id: slot?.slot_id || slot?.id || `${dateKey}-${slot?.start_time || slot?.startTime}`,
      label: formatSlotTimeRange(slot),
      dateKey,
      startDate,
      raw: slot,
    };

    if (!groupedSlots.has(dateKey)) {
      groupedSlots.set(dateKey, {
        dateKey,
        date: startDate,
        dayNumber: day,
        monthKey,
        monthDate,
        displayDate: formatAustralianDateLabel(startDate),
        slots: [],
      });
    }

    groupedSlots.get(dateKey).slots.push(slotEntry);
  });

  const dates = Array.from(groupedSlots.values()).sort(
    (left, right) => left.date.getTime() - right.date.getTime()
  );

  const months = Array.from(
    dates.reduce((monthMap, item) => {
      if (!monthMap.has(item.monthKey)) {
        monthMap.set(item.monthKey, {
          key: item.monthKey,
          date: item.monthDate,
          label: formatMonthLabel(item.monthDate),
        });
      }
      return monthMap;
    }, new Map()).values()
  );

  return { dates, months };
};

const mergePendingSlotIntoAvailability = ({
  availabilitySlots = [],
  pendingBooking = null,
  fallbackSelectedSlot = null,
}) => {
  const baseSlots = Array.isArray(availabilitySlots) ? availabilitySlots : [];
  if (!pendingBooking || isBookingPaidOrConfirmed(pendingBooking)) {
    return baseSlots;
  }

  const pendingSlot =
    pendingBooking?.slot ||
    pendingBooking?.booking?.slot ||
    fallbackSelectedSlot ||
    null;

  if (!pendingSlot || typeof pendingSlot !== "object") {
    return baseSlots;
  }

  const pendingSlotId = String(
    pendingSlot?.slot_id || pendingSlot?.id || ""
  ).trim();
  const pendingStartTime = String(
    pendingSlot?.start_time || pendingSlot?.startTime || ""
  ).trim();

  if (!pendingSlotId && !pendingStartTime) {
    return baseSlots;
  }

  const alreadyPresent = baseSlots.some((slot) => {
    const slotId = String(slot?.slot_id || slot?.id || "").trim();
    const startTime = String(slot?.start_time || slot?.startTime || "").trim();

    return (
      (pendingSlotId && slotId === pendingSlotId) ||
      (pendingStartTime && startTime === pendingStartTime)
    );
  });

  if (alreadyPresent) {
    return baseSlots;
  }

  return [
    ...baseSlots,
    {
      ...pendingSlot,
      id: pendingSlot?.id || pendingSlotId,
      slot_id: pendingSlot?.slot_id || pendingSlotId,
      start_time: pendingSlot?.start_time || pendingSlot?.startTime,
      end_time: pendingSlot?.end_time || pendingSlot?.endTime,
      pending_booking_id: String(
        pendingBooking?.booking_id ||
          pendingBooking?.bookingId ||
          pendingBooking?.id ||
          pendingBooking?.booking?.booking_id ||
          pendingBooking?.booking?.bookingId ||
          pendingBooking?.booking?.id ||
          ""
      ).trim(),
      is_pending_payment: true,
    },
  ];
};

const buildTutorModel = (teacher = {}, fallbackTutorId = "") => {
  if (!teacher || typeof teacher !== "object" || !Object.keys(teacher).length) {
    return null;
  }

  const rawPricePerSession = teacher?.price_per_session ?? teacher?.price;
  const pricePerSession = normalizeAudPriceForDisplay(rawPricePerSession);
  const yearIds = Array.isArray(teacher?.year)
    ? teacher.year
        .map((entry) => Number(entry?.id ?? entry))
        .filter((entry) => Number.isFinite(entry) && entry > 0)
    : Array.isArray(teacher?.yearIds)
      ? teacher.yearIds
          .map((entry) => Number(entry))
          .filter((entry) => Number.isFinite(entry) && entry > 0)
      : [];

  return {
    ...teacher,
    id: teacher?.id || fallbackTutorId || "",
    name:
      teacher?.name ||
      teacher?.full_name ||
      teacher?.teacher_name ||
      "Tutor not selected",
    image: teacher?.photoImage || teacher?.photo || teacher?.image || "",
    price: pricePerSession ?? 0,
    priceMinorUnits: normalizeAudPriceToMinorUnits(rawPricePerSession),
    yearIds,
  };
};

const getBookingIdFromPayload = (payload = {}) =>
  String(
    payload?.booking_id ||
      payload?.bookingId ||
      payload?.id ||
      payload?.booking?.booking_id ||
      payload?.booking?.bookingId ||
      payload?.booking?.id ||
      ""
  ).trim();

const getHeldBookingSlotId = (payload = {}) =>
  String(
    payload?.slot?.id ||
      payload?.slot?.slot_id ||
      payload?.slot_id ||
      payload?.booking?.slot?.id ||
      payload?.booking?.slot?.slot_id ||
      payload?.booking?.slot_id ||
      ""
  ).trim();

const getHeldBookingStartTime = (payload = {}) =>
  String(
    payload?.slot?.start_time ||
      payload?.slot?.startTime ||
      payload?.booking?.slot?.start_time ||
      payload?.booking?.slot?.startTime ||
      ""
  ).trim();

const getHeldBookingYearId = (payload = {}) =>
  Number(
    payload?.year?.id ||
      payload?.year_id ||
      payload?.booking?.year?.id ||
      payload?.booking?.year_id ||
      0
  );

const normalizeBookingStatusValue = (value) =>
  String(value || "").trim().toLowerCase();

const getBookingStatusFromPayload = (payload = {}) =>
  normalizeBookingStatusValue(payload?.status || payload?.booking?.status);

const getPaymentStatusFromPayload = (payload = {}) =>
  normalizeBookingStatusValue(
    payload?.payment_status || payload?.booking?.payment_status
  );

const getPaidAtFromPayload = (payload = {}) =>
  payload?.paid_at || payload?.booking?.paid_at || "";

const isBookingPaidOrConfirmed = (payload = {}) =>
  getPaymentStatusFromPayload(payload) === "paid" ||
  getBookingStatusFromPayload(payload) === "confirmed";

const isSamePendingBookingSelection = ({
  booking,
  selectedSlotId,
  selectedStartTime,
  selectedYearId,
}) => {
  if (!booking || isBookingPaidOrConfirmed(booking)) {
    return false;
  }

  const bookingSlotId = getHeldBookingSlotId(booking);
  const bookingStartTime = getHeldBookingStartTime(booking);
  const bookingYearId = getHeldBookingYearId(booking);

  const matchesSlot =
    (selectedSlotId && bookingSlotId === String(selectedSlotId).trim()) ||
    (selectedStartTime &&
      bookingStartTime === String(selectedStartTime).trim());
  const matchesYear =
    !selectedYearId ||
    !bookingYearId ||
    bookingYearId === Number(selectedYearId);

  return Boolean(matchesSlot && matchesYear && getBookingIdFromPayload(booking));
};

const normalizeHeldBookingPayload = (payload = {}) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const bookingId = getBookingIdFromPayload(payload);
  const nestedBooking = payload?.booking && typeof payload.booking === "object"
    ? payload.booking
    : null;

  return {
    ...nestedBooking,
    ...payload,
    booking_id: bookingId || nestedBooking?.booking_id || "",
    bookingId: bookingId || nestedBooking?.bookingId || "",
    slot:
      payload?.slot ||
      nestedBooking?.slot ||
      (payload?.slot_id ? { id: payload.slot_id } : undefined),
    year:
      payload?.year ||
      nestedBooking?.year ||
      (payload?.year_id ? { id: payload.year_id } : undefined),
    status:
      payload?.status ||
      nestedBooking?.status ||
      "",
    payment_status:
      payload?.payment_status ||
      nestedBooking?.payment_status ||
      "",
    paid_at: getPaidAtFromPayload(payload),
  };
};

const buildBookingConfirmedPath = ({ bookingId, tutorId, checkoutSessionId }) =>
  `/classBooking/bookingConfirmed?bookingId=${encodeURIComponent(
    bookingId || ""
  )}&tutorId=${encodeURIComponent(tutorId || "")}&session_id=${encodeURIComponent(
    checkoutSessionId || ""
  )}&checkout_session_id=${encodeURIComponent(checkoutSessionId || "")}`;

const buildBookingPagePath = ({ bookingId, tutorId }) => {
  const params = new URLSearchParams();
  if (bookingId) {
    params.set("bookingId", bookingId);
  }
  if (tutorId) {
    params.set("tutorId", tutorId);
  }

  const search = params.toString();
  return search ? `/classBooking/booking?${search}` : "/classBooking/booking";
};

const normalizeTutorIdentifier = (value) => String(value || "").trim();

const getPendingTutorBookings = ({
  bookingFlowCache = {},
  tutorId = "",
  activeSummary = null,
}) => {
  const normalizedTutorId = normalizeTutorIdentifier(tutorId);
  const candidateSummaries = [
    activeSummary,
    ...Object.values(bookingFlowCache || {}),
  ].filter((entry) => entry && typeof entry === "object");
  const seenBookingIds = new Set();

  return candidateSummaries.reduce((result, summary) => {
    const summaryTutorId = normalizeTutorIdentifier(
      summary?.tutorId || summary?.tutor?.id
    );
    if (normalizedTutorId && summaryTutorId && summaryTutorId !== normalizedTutorId) {
      return result;
    }

    const bookingPayload = normalizeHeldBookingPayload(
      summary?.heldBooking || summary
    );
    const bookingId = getBookingIdFromPayload(bookingPayload);
    if (!bookingPayload || !bookingId || seenBookingIds.has(bookingId)) {
      return result;
    }

    if (isBookingPaidOrConfirmed(bookingPayload) || isBookingPaidOrConfirmed(summary)) {
      return result;
    }

    seenBookingIds.add(bookingId);
    result.push({
      booking: bookingPayload,
      selectedSlot: summary?.selectedSlot || null,
    });
    return result;
  }, []);
};

const BookingPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentYear } = useSelector((state) => state.profile);
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const bookingIdFromQuery = String(searchParams.get("bookingId") || "").trim();
  const queryTutorId = String(searchParams.get("tutorId") || "").trim();
  const storedBookingSummary = useMemo(
    () => readStoredBookingSummary(bookingIdFromQuery),
    [bookingIdFromQuery]
  );
  const bookingFlowCache = readBookingFlowCache();
  const activeBookingId =
    bookingIdFromQuery ||
    String(
      storedBookingSummary?.bookingId ||
        storedBookingSummary?.heldBooking?.booking_id ||
        ""
    ).trim();
  const stateTutor = location.state?.tutor || null;
  const tutorId =
    location.state?.tutorId ||
    queryTutorId ||
    stateTutor?.id ||
    storedBookingSummary?.tutorId ||
    storedBookingSummary?.tutor?.id ||
    null;
  const isPaymentCancelled = useMemo(
    () => searchParams.get("payment") === "cancelled",
    [searchParams]
  );
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [selectedTimeId, setSelectedTimeId] = useState(null);
  const [bookingStatus, setBookingStatus] = useState({ type: "", message: "" });
  const [heldBooking, setHeldBooking] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentUiState, setPaymentUiState] = useState(PAYMENT_UI_STATES.IDLE);
  const activeCheckoutBookingIdRef = useRef("");
  const tutorDetailQuery = useQuery({
    queryKey: ["class-booking-tutor-detail", tutorId],
    queryFn: () => teacherApi.getTutorDetail(tutorId),
    enabled: Boolean(tutorId) && !stateTutor && !storedBookingSummary?.tutor,
    retry: false,
  });
  const bookingTutor = useMemo(
    () =>
      buildTutorModel(stateTutor, tutorId) ||
      buildTutorModel(storedBookingSummary?.tutor, tutorId) ||
      buildTutorModel(tutorDetailQuery.data, tutorId),
    [stateTutor, storedBookingSummary?.tutor, tutorDetailQuery.data, tutorId]
  );

  const availabilityQuery = useQuery({
    queryKey: ["booking-tutor-availability", tutorId],
    queryFn: () => teacherApi.getTutorAvailability(tutorId),
    enabled: Boolean(tutorId),
    retry: false,
  });

  const normalizedApiAvailability = useMemo(
    () => normalizeAvailabilityCollection(availabilityQuery.data),
    [availabilityQuery.data]
  );

  const pendingTutorBookings = useMemo(
    () =>
      getPendingTutorBookings({
        bookingFlowCache,
        tutorId,
        activeSummary: storedBookingSummary,
      }),
    [bookingFlowCache, storedBookingSummary, tutorId]
  );
  const pendingHeldBooking = useMemo(
    () =>
      normalizeHeldBookingPayload(
        heldBooking ||
          storedBookingSummary?.heldBooking ||
          null
      ),
    [heldBooking, storedBookingSummary]
  );
  const availabilitySlots = useMemo(
    () => {
      const mergedPendingSlots = pendingTutorBookings.reduce(
        (nextSlots, entry) =>
          mergePendingSlotIntoAvailability({
            availabilitySlots: nextSlots,
            pendingBooking: entry.booking,
            fallbackSelectedSlot: entry.selectedSlot,
          }),
        normalizedApiAvailability
      );

      return mergePendingSlotIntoAvailability({
        availabilitySlots: mergedPendingSlots,
        pendingBooking: pendingHeldBooking,
        fallbackSelectedSlot: storedBookingSummary?.selectedSlot || null,
      });
    },
    [
      normalizedApiAvailability,
      pendingHeldBooking,
      pendingTutorBookings,
      storedBookingSummary,
    ]
  );
  const availabilityModel = useMemo(
    () => buildAvailabilityModel(availabilitySlots),
    [availabilitySlots]
  );
  const [activeMonthIndex, setActiveMonthIndex] = useState(0);

  useEffect(() => {
    setActiveMonthIndex(0);
  }, [availabilityModel.months]);

  useEffect(() => {
    if (!availabilityModel.dates.length) {
      setSelectedDateKey(null);
      setSelectedTimeId(null);
      return;
    }

    const hasSelectedDate = availabilityModel.dates.some(
      (item) => item.dateKey === selectedDateKey
    );

    if (!hasSelectedDate) {
      setSelectedDateKey(availabilityModel.dates[0].dateKey);
      setSelectedTimeId(null);
    }
  }, [availabilityModel.dates, selectedDateKey]);

  const activeMonth = availabilityModel.months[activeMonthIndex] || null;
  const month = activeMonth?.label || formatMonthLabel(new Date());
  const activeMonthDates = useMemo(
    () =>
      availabilityModel.dates.filter(
        (item) => item.monthKey === activeMonth?.key
      ),
    [availabilityModel.dates, activeMonth]
  );
  const daysInMonth = useMemo(() => {
    if (!activeMonth?.date) {
      return Array.from({ length: 31 }, (_, index) => index + 1);
    }

    const totalDays = new Date(
      activeMonth.date.getFullYear(),
      activeMonth.date.getMonth() + 1,
      0
    ).getDate();

    return Array.from({ length: totalDays }, (_, index) => index + 1);
  }, [activeMonth]);

  const availableDateMap = useMemo(
    () =>
      activeMonthDates.reduce((map, item) => {
        map.set(item.dayNumber, item);
        return map;
      }, new Map()),
    [activeMonthDates]
  );

  const selectedDate = useMemo(
    () =>
      availabilityModel.dates.find((item) => item.dateKey === selectedDateKey) || null,
    [availabilityModel.dates, selectedDateKey]
  );
  const timeSlots = useMemo(() => selectedDate?.slots || [], [selectedDate]);
  const selectedTime = useMemo(
    () => timeSlots.find((slot) => slot.id === selectedTimeId) || null,
    [timeSlots, selectedTimeId]
  );
  const isPaymentEnabled = Boolean(selectedDate && selectedTime && tutorId);
  const tutor = bookingTutor || {
    id: tutorId || "",
    name: "Tutor not selected",
    price: 0,
    image: "",
    yearIds: [],
  };
  const selectedYearId =
    Number(location.state?.yearId) ||
    Number(storedBookingSummary?.yearId) ||
    getCanonicalYearId(currentYear) ||
    tutor.yearIds?.[0] ||
    0;

  const formattedDate = selectedDate?.displayDate || "Not selected";
  const selectedTimeLabel = selectedTime?.label || "Not selected";
  const duration = selectedTime ? getSlotDurationLabel(selectedTime.raw) : "60 minutes";
  const selectedSlotId = selectedTime?.raw?.slot_id || selectedTime?.raw?.id || "";
  const selectedSlotStartTime = String(
    selectedTime?.raw?.start_time || selectedTime?.raw?.startTime || ""
  ).trim();
  const selectedPendingBookingId = String(
    selectedTime?.raw?.pending_booking_id || ""
  ).trim();
  const selectedPendingBooking = useMemo(
    () => {
      if (selectedPendingBookingId) {
        const directMatch = pendingTutorBookings.find(
          (entry) => getBookingIdFromPayload(entry?.booking) === selectedPendingBookingId
        );
        if (directMatch?.booking) {
          return directMatch.booking;
        }
      }

      return (
        pendingTutorBookings.find((entry) =>
          isSamePendingBookingSelection({
            booking: entry?.booking,
            selectedSlotId,
            selectedStartTime: selectedSlotStartTime,
            selectedYearId,
          })
        )?.booking || null
      );
    },
    [
      pendingTutorBookings,
      selectedPendingBookingId,
      selectedSlotId,
      selectedSlotStartTime,
      selectedYearId,
    ]
  );
  const activeBookingContext =
    heldBooking || selectedPendingBooking || storedBookingSummary || null;
  const activeBookingPaymentStatus = getPaymentStatusFromPayload(activeBookingContext);
  const activeBookingStatus = getBookingStatusFromPayload(activeBookingContext);
  const isCurrentBookingPaidOrConfirmed = Boolean(
    isBookingPaidOrConfirmed(activeBookingContext)
  );
  const holdBookingMutation = useMutation({
    mutationFn: () =>
      bookingService.holdBooking({
        tutor_id: tutorId,
        slot_id: selectedSlotId,
        year_id: selectedYearId,
      }),
  });
  const checkoutSessionMutation = useMutation({
    mutationFn: ({ bookingId }) =>
      bookingService.createCheckoutSession({
        booking_id: bookingId,
        frontend_base_url: window.location.origin,
        success_url: `${window.location.origin}/classBooking/bookingConfirmed?bookingId=${bookingId}&tutorId=${encodeURIComponent(
          tutorId || ""
        )}&session_id={CHECKOUT_SESSION_ID}&checkout_session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/classBooking/booking?bookingId=${bookingId}&tutorId=${encodeURIComponent(
          tutorId || ""
        )}&payment=cancelled`,
      }),
  });

  useEffect(() => {
    if (heldBooking || !storedBookingSummary?.heldBooking) {
      return;
    }

    const normalizedStoredHold = normalizeHeldBookingPayload(
      storedBookingSummary.heldBooking
    );
    const storedHoldBookingId = getBookingIdFromPayload(normalizedStoredHold);
    if (
      normalizedStoredHold &&
      (!activeBookingId || storedHoldBookingId === activeBookingId)
    ) {
      setHeldBooking(normalizedStoredHold);
    }
  }, [activeBookingId, heldBooking, storedBookingSummary]);

  useEffect(() => {
    if (!storedBookingSummary?.heldBooking || !availabilityModel.dates.length) {
      return;
    }

    const storedHold = normalizeHeldBookingPayload(storedBookingSummary.heldBooking);
    const storedHoldBookingId = getBookingIdFromPayload(storedHold);
    if (activeBookingId && storedHoldBookingId && storedHoldBookingId !== activeBookingId) {
      return;
    }
    const storedSlotId = getHeldBookingSlotId(storedHold);
    const storedStartTime =
      storedHold?.slot?.start_time ||
      storedHold?.booking?.slot?.start_time ||
      "";

    const matchingDate = availabilityModel.dates.find((dateItem) =>
      dateItem.slots.some((slot) => {
        const candidateSlotId = String(
          slot?.raw?.slot_id || slot?.raw?.id || ""
        ).trim();
        const candidateStartTime = String(
          slot?.raw?.start_time || slot?.raw?.startTime || ""
        ).trim();

        return (
          (storedSlotId && candidateSlotId === storedSlotId) ||
          (storedStartTime && candidateStartTime === String(storedStartTime).trim())
        );
      })
    );

    if (!matchingDate) {
      return;
    }

    const matchingSlot = matchingDate.slots.find((slot) => {
      const candidateSlotId = String(
        slot?.raw?.slot_id || slot?.raw?.id || ""
      ).trim();
      const candidateStartTime = String(
        slot?.raw?.start_time || slot?.raw?.startTime || ""
      ).trim();

      return (
        (storedSlotId && candidateSlotId === storedSlotId) ||
        (storedStartTime && candidateStartTime === String(storedStartTime).trim())
      );
    });

    if (!matchingSlot) {
      return;
    }

    const shouldRestoreStoredSelection =
      !selectedDateKey &&
      !selectedTimeId;

    if (shouldRestoreStoredSelection) {
      setSelectedDateKey(matchingDate.dateKey);
      setSelectedTimeId(matchingSlot.id);
    }
  }, [
    activeBookingId,
    availabilityModel.dates,
    selectedDateKey,
    selectedTimeId,
    storedBookingSummary,
  ]);

  useEffect(() => {
    if (isPaymentCancelled && !isCurrentBookingPaidOrConfirmed) {
      setBookingStatus({
        type: "error",
        message: "Payment was cancelled. You can choose the slot again or continue when ready.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
    }
  }, [isCurrentBookingPaidOrConfirmed, isPaymentCancelled]);

  useEffect(() => {
    if (!isPaymentCancelled || !isCurrentBookingPaidOrConfirmed) {
      return;
    }

    setBookingStatus({ type: "", message: "" });
    navigate(
      buildBookingPagePath({
        bookingId: bookingIdFromQuery,
        tutorId,
      }),
      { replace: true }
    );
  }, [
    bookingIdFromQuery,
    isCurrentBookingPaidOrConfirmed,
    isPaymentCancelled,
    navigate,
    tutorId,
  ]);

  useEffect(() => {
    setHeldBooking(null);
    setIsPaymentModalOpen(false);
    if (!isPaymentCancelled) {
      setPaymentUiState(PAYMENT_UI_STATES.IDLE);
    }
  }, [isPaymentCancelled, selectedDateKey, selectedTimeId, selectedYearId, tutorId]);

  const handleHoldBookingError = async (error) => {
    const status = Number(error?.status || 0);
    const message =
      error?.message || "Unable to hold the booking slot right now.";

    if ([400, 404, 409].includes(status)) {
      setSelectedTimeId(null);
      setHeldBooking(null);
      await queryClient.invalidateQueries({
        queryKey: ["booking-tutor-availability", tutorId],
      });
    }

    if (status === 409 || status === 400) {
      setBookingStatus({
        type: "error",
        message: message.toLowerCase().includes("not active")
          ? "That slot is no longer active. Please choose another time."
          : "That slot is no longer available. Please choose another time.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    if (status === 401) {
      setBookingStatus({
        type: "error",
        message: "Student authentication is required. Please log in again.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    setBookingStatus({ type: "error", message });
    setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
  };

  const handleCheckoutError = async (error, bookingId) => {
    const status = Number(error?.status || 0);
    const message =
      error?.message || "Unable to create the Stripe checkout session.";
    const conflictPayload =
      error?.raw?.data && typeof error.raw.data === "object"
        ? normalizeHeldBookingPayload(error.raw.data)
        : null;

    activeCheckoutBookingIdRef.current = "";

    if (status === 401) {
      setBookingStatus({
        type: "error",
        message: "Student authentication is required. Please log in again.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    if (status === 404) {
      setBookingStatus({
        type: "error",
        message,
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    if (
      status === 409 &&
      message.toLowerCase().includes("already been paid")
    ) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["student-class-booking-dashboard"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["student-class-booking-list"],
        }),
      ]);

      if (conflictPayload) {
        setHeldBooking(conflictPayload);
        persistBookingSummary({
          ...storedBookingSummary,
          bookingId: bookingId || getBookingIdFromPayload(conflictPayload),
          heldBooking: conflictPayload,
          tutor: bookingTutor || storedBookingSummary?.tutor || tutor,
          tutorId: tutorId || storedBookingSummary?.tutorId || "",
          yearId: selectedYearId || storedBookingSummary?.yearId || "",
          formattedDate,
          selectedTime: selectedTimeLabel,
          duration,
          status: conflictPayload.status || storedBookingSummary?.status || "confirmed",
          payment_status:
            conflictPayload.payment_status ||
            storedBookingSummary?.payment_status ||
            "paid",
          paid_at:
            conflictPayload.paid_at || storedBookingSummary?.paid_at || "",
        });
      }

      toast.info("This session has already been paid.");
      setBookingStatus({
        type: "success",
        message: "This session has already been paid.",
      });
      setIsPaymentModalOpen(false);
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_CONFIRMED);

      const conflictCheckoutSessionId = String(
        conflictPayload?.checkout_session_id ||
          conflictPayload?.session_id ||
          conflictPayload?.booking?.checkout_session_id ||
          ""
      ).trim();

      if (bookingId && conflictCheckoutSessionId) {
        navigate(
          buildBookingConfirmedPath({
            bookingId,
            tutorId,
            checkoutSessionId: conflictCheckoutSessionId,
          }),
          { replace: true }
        );
        return;
      }

      navigate("/classBooking/dashboard", { replace: true });
      return;
    }

    if (status === 409) {
      setSelectedTimeId(null);
      setHeldBooking(null);
      await queryClient.invalidateQueries({
        queryKey: ["booking-tutor-availability", tutorId],
      });
      setBookingStatus({
        type: "error",
        message: "This booking slot is no longer available. Please choose another time.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    setBookingStatus({ type: "error", message });
    setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
  };

  const handleStartPaymentConfirmation = () => {
    if (
      !isPaymentEnabled ||
      holdBookingMutation.isPending ||
      checkoutSessionMutation.isPending
    ) {
      return;
    }

    if (isCurrentBookingPaidOrConfirmed) {
      toast.info("This session has already been paid.");
      return;
    }

    if (!selectedYearId) {
      setBookingStatus({
        type: "error",
        message: "Please select a valid student year before continuing.",
      });
      return;
    }

    setBookingStatus({ type: "", message: "" });
    if (isPaymentCancelled) {
      navigate(
        buildBookingPagePath({
          bookingId: bookingIdFromQuery,
          tutorId,
        }),
        { replace: true }
      );
    }
    setIsPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    if (holdBookingMutation.isPending || checkoutSessionMutation.isPending) {
      return;
    }

    setIsPaymentModalOpen(false);
  };

  const handleContinueToPayment = async () => {
    if (
      !isPaymentEnabled ||
      holdBookingMutation.isPending ||
      checkoutSessionMutation.isPending
    ) {
      return;
    }

    if (!selectedYearId) {
      setBookingStatus({
        type: "error",
        message: "Please select a valid student year before continuing.",
      });
      return;
    }

    setBookingStatus({ type: "", message: "" });
    let activeHeldBooking = null;
    setPaymentUiState(PAYMENT_UI_STATES.CREATING_CHECKOUT);

    if (
      getBookingIdFromPayload(heldBooking) &&
      isSamePendingBookingSelection({
        booking: heldBooking,
        selectedSlotId,
        selectedStartTime: selectedSlotStartTime,
        selectedYearId,
      })
    ) {
      activeHeldBooking = heldBooking;
    } else if (selectedPendingBooking) {
      activeHeldBooking = selectedPendingBooking;
      setHeldBooking(selectedPendingBooking);
    } else {
      try {
        const holdPayload = await holdBookingMutation.mutateAsync();
        activeHeldBooking = normalizeHeldBookingPayload(holdPayload);
        setHeldBooking(activeHeldBooking);
      } catch (error) {
        const holdErrorMessage = String(error?.message || "").toLowerCase();
        const reusablePendingBooking =
          selectedPendingBooking ||
          pendingTutorBookings.find((entry) =>
            isSamePendingBookingSelection({
              booking: entry?.booking,
              selectedSlotId,
              selectedStartTime: selectedSlotStartTime,
              selectedYearId,
            })
          )?.booking ||
          null;

        if (
          reusablePendingBooking &&
          (holdErrorMessage.includes("already reserved or booked") ||
            holdErrorMessage.includes("already reserved") ||
            holdErrorMessage.includes("already booked"))
        ) {
          activeHeldBooking = reusablePendingBooking;
          setHeldBooking(reusablePendingBooking);
          setBookingStatus({ type: "", message: "" });
        } else {
          await handleHoldBookingError(error);
          return;
        }
      }
    }

    const bookingId = getBookingIdFromPayload(activeHeldBooking);
    if (!bookingId) {
      setBookingStatus({
        type: "error",
        message: "Booking ID is missing. Please try again.",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      return;
    }

    if (isBookingPaidOrConfirmed(activeHeldBooking)) {
      toast.info("This session has already been paid.");
      setBookingStatus({
        type: "success",
        message: "This session has already been paid.",
      });
      setIsPaymentModalOpen(false);
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_CONFIRMED);
      return;
    }

    if (activeCheckoutBookingIdRef.current === bookingId) {
      return;
    }

    activeCheckoutBookingIdRef.current = bookingId;

    try {
      const checkoutPayload = await checkoutSessionMutation.mutateAsync({
        bookingId,
      });
      const checkoutData = checkoutPayload || {};
      const checkoutUrl = checkoutData?.checkout_url || "";

      if (!checkoutUrl) {
        setBookingStatus({
          type: "error",
          message: "Stripe checkout URL is missing. Please try again.",
        });
        activeCheckoutBookingIdRef.current = "";
        setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
        return;
      }

      const bookingSummaryPayload = {
        tutor: {
          ...tutor,
          price:
            normalizeAudPriceForDisplay(checkoutData?.amount) ||
            Number(checkoutData?.booking?.session_fee) ||
            Number(activeHeldBooking?.tutor?.price_per_session ?? tutor.price) ||
            tutor.price,
        },
        formattedDate,
        selectedTime: selectedTimeLabel,
        duration,
        bookingId,
        heldBooking: activeHeldBooking,
        selectedSlot: selectedTime?.raw || storedBookingSummary?.selectedSlot || null,
        tutorId,
        yearId: selectedYearId,
        status:
          checkoutData?.booking?.status ||
          activeHeldBooking?.status ||
          storedBookingSummary?.status ||
          "",
        payment_status:
          checkoutData?.payment_status ||
          checkoutData?.booking?.payment_status ||
          activeHeldBooking?.payment_status ||
          "pending",
        paid_at: getPaidAtFromPayload(checkoutData) || "",
        checkout_session_id:
          checkoutData?.checkout_session_id || storedBookingSummary?.checkout_session_id || "",
      };

      persistBookingSummary(bookingSummaryPayload);
      setIsPaymentModalOpen(false);
      setPaymentUiState(PAYMENT_UI_STATES.REDIRECTING);
      window.location.assign(checkoutUrl);
    } catch (error) {
      await handleCheckoutError(error, bookingId);
    }
  };

  const canTriggerPayment = Boolean(
    isPaymentEnabled &&
      !checkoutSessionMutation.isPending &&
      !holdBookingMutation.isPending &&
      !isCurrentBookingPaidOrConfirmed &&
      paymentUiState !== PAYMENT_UI_STATES.CREATING_CHECKOUT &&
      paymentUiState !== PAYMENT_UI_STATES.REDIRECTING
  );
  const summaryContinueLabel = isCurrentBookingPaidOrConfirmed
    ? "Paid"
    : paymentUiState === PAYMENT_UI_STATES.CREATING_CHECKOUT ||
        paymentUiState === PAYMENT_UI_STATES.REDIRECTING
      ? "Redirecting to secure checkout..."
      : isPaymentCancelled
        ? "Try Again"
        : "Continue to Payment";
  const summaryBadgeLabel = isCurrentBookingPaidOrConfirmed
    ? "Paid"
    : activeBookingPaymentStatus === "pending"
      ? "Payment Pending"
      : null;
  const summaryStatusDescription = isCurrentBookingPaidOrConfirmed
    ? "This booking has already been paid and confirmed."
    : activeBookingPaymentStatus === "pending"
      ? "Payment is still pending for this booking."
      : "";

  return (
    <PageContainer>
 <div className="sticky top-0 z-50 bg-[#DFF2EB]/70 backdrop-blur-md">
  <Header backLabel="Back to Profile" />
</div>


      <MainContent className="pb-20 px-12">
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Select Date & Time</h2>
            <p className="text-gray-600 mt-1">
              Choose an available slot with {tutor.name}
            </p>
          </div>

          {!bookingTutor && tutorDetailQuery.isLoading ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-600 shadow-sm">
              Loading tutor details...
            </div>
          ) : null}

          {!bookingTutor && !tutorDetailQuery.isLoading ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 shadow-sm">
              Tutor information is missing. Go back to the tutor details page and choose
              a tutor again.
            </div>
          ) : null}

          {bookingStatus.message ? (
            <div
              className={`rounded-2xl p-5 shadow-sm ${
                bookingStatus.type === "error"
                  ? "border border-red-200 bg-red-50 text-red-700"
                  : "border border-green-200 bg-green-50 text-green-700"
              }`}
            >
              {bookingStatus.message}
            </div>
          ) : null}

          <div className="grid items-stretch gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
            <div className="flex h-full flex-col bg-white rounded-2xl p-8 shadow-md border border-gray-200">
              <div className="flex items-center gap-2 text-gray-800 font-medium">
                <Calendar size={20} />
                Select Date
              </div>

              <div className="flex justify-between items-center mt-6">
                <button
                  className={`p-2 rounded-full ${
                    activeMonthIndex > 0 ? "bg-gray-100" : "bg-gray-100/60 opacity-50"
                  }`}
                  type="button"
                  onClick={() => setActiveMonthIndex((current) => Math.max(0, current - 1))}
                  disabled={activeMonthIndex <= 0}
                >
                  <ChevronLeft size={18} className="text-gray-600" />
                </button>

                <h3 className="text-lg font-semibold">{month}</h3>

                <button
                  className={`p-2 rounded-full ${
                    activeMonthIndex < availabilityModel.months.length - 1
                      ? "bg-[#0736D6]"
                      : "bg-[#0736D6]/50 opacity-50"
                  }`}
                  type="button"
                  onClick={() =>
                    setActiveMonthIndex((current) =>
                      Math.min(availabilityModel.months.length - 1, current + 1)
                    )
                  }
                  disabled={activeMonthIndex >= availabilityModel.months.length - 1}
                >
                  <ChevronRight size={18} className="text-white" />
                </button>
              </div>

              <div className="grid grid-cols-7 text-center text-gray-500 font-medium mt-6">
                <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span>
                <span>Thu</span><span>Fri</span><span>Sat</span>
              </div>

              <div className="grid grid-cols-7 text-center mt-4 gap-y-4">
                {daysInMonth.map((day) => {
                  const availableDate = availableDateMap.get(day);
                  const available = Boolean(availableDate);
                  const selected = selectedDate?.dayNumber === day;
                  const dayButtonClass = selected
                    ? "bg-[#0736D6] text-white"
                    : available
                      ? "bg-gray-100 text-gray-800 hover:bg-gray-200"
                      : "text-gray-300";

                  return (
                    <button
                      key={day}
                      disabled={!available}
                      onClick={() => {
                        setSelectedDateKey(availableDate?.dateKey || null);
                        setSelectedTimeId(null);
                      }}
                      className={`
                        w-10 h-10 flex items-center justify-center rounded-full mx-auto transition
                        ${dayButtonClass}
                      `}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              <div className="mt-10 mb-3 flex items-center gap-2 text-gray-700 font-medium">
                <Clock size={18} /> Available Times
              </div>

              {availabilityQuery.isLoading ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  Loading available slots...
                </div>
              ) : availabilityQuery.isError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {availabilityQuery.error.message || "Unable to load tutor availability."}
                </div>
              ) : !availabilityModel.dates.length ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  No available dates or slots for this tutor.
                </div>
              ) : (
                <div className="flex gap-4 flex-wrap">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.id}
                      disabled={!selectedDate}
                      onClick={() => setSelectedTimeId(slot.id)}
                      className={`
                        px-6 py-2 rounded-xl border text-sm transition
                        ${
                          selectedTime?.id === slot.id
                            ? "border-[#0736D6] bg-[#EAEFFF] text-[#0736D6] font-semibold"
                            : "border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
                        }
                        ${!selectedDate ? "cursor-not-allowed opacity-50 hover:bg-white" : ""}
                      `}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex">
              <BookingSummary 
                tutor={tutor}
                selectedTime={selectedTimeLabel}
                formattedDate={formattedDate}
                duration={duration}
                onContinue={handleStartPaymentConfirmation}
                isPaymentEnabled={canTriggerPayment}
                continueLabel={summaryContinueLabel}
                paymentBadgeLabel={summaryBadgeLabel}
                paymentBadgeTone={
                  isCurrentBookingPaidOrConfirmed ? "success" : "warning"
                }
                statusDescription={summaryStatusDescription}
                isPaymentDisabled={isCurrentBookingPaidOrConfirmed}
                bookingStatusLabel={activeBookingStatus}
              />
            </div>
          </div>
        </div>
      </MainContent>
      {isPaymentModalOpen ? (
        <PaymentModal
          tutor={tutor}
          formattedDate={formattedDate}
          selectedTime={selectedTimeLabel}
          duration={duration}
          onClose={handleClosePaymentModal}
          onConfirm={handleContinueToPayment}
          isSubmitting={
            holdBookingMutation.isPending ||
            checkoutSessionMutation.isPending ||
            paymentUiState === PAYMENT_UI_STATES.CREATING_CHECKOUT ||
            paymentUiState === PAYMENT_UI_STATES.REDIRECTING
          }
          confirmLabel={
            paymentUiState === PAYMENT_UI_STATES.CREATING_CHECKOUT ||
            paymentUiState === PAYMENT_UI_STATES.REDIRECTING
              ? "Redirecting to secure checkout..."
              : "Confirm & Pay"
          }
        />
      ) : null}
    </PageContainer>
  );
};

export default BookingPage;
